import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Inbox, CalendarDays, CalendarRange, BellRing, Mail } from "lucide-react";
import api from "@/lib/api";

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-analytics"],
    queryFn: async () => (await api.get("/admin/analytics")).data,
  });

  if (isLoading) return <p className="text-slateblue text-sm">Loading analytics…</p>;

  const stats = [
    { label: "Total Estimates", value: data.total, icon: Inbox, to: "/admin/estimates" },
    { label: "This Week", value: data.week, icon: CalendarDays, to: "/admin/estimates" },
    { label: "This Month", value: data.month, icon: CalendarRange, to: "/admin/estimates" },
    { label: "New Requests", value: data.new, icon: BellRing, to: "/admin/estimates" },
    { label: "Unread Messages", value: data.unread_messages, icon: Mail, to: "/admin/messages" },
  ];

  const maxType = Math.max(1, ...Object.values(data.by_type));
  const statuses = ["new", "contacted", "quoted", "won", "lost"];
  const statusColors = { new: "bg-orange", contacted: "bg-blue-500", quoted: "bg-amber-500", won: "bg-green-600", lost: "bg-red-500" };

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading font-bold text-2xl tracking-tight mb-8">Dashboard</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-10">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
            className="bg-white border border-sandline p-5 hover:border-orange/50 transition-colors duration-200 shadow-[0_8px_24px_-16px_rgba(11,19,32,0.25)]">
            <s.icon size={20} className="text-orange mb-3" strokeWidth={1.6} />
            <p className="font-heading font-black text-3xl">{s.value}</p>
            <p className="text-xs font-bold uppercase tracking-widest text-slateblue mt-1">{s.label}</p>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-sandline p-6" data-testid="by-type-card">
          <h2 className="font-heading font-bold text-lg tracking-tight mb-6">Estimates by Project Type</h2>
          {Object.keys(data.by_type).length === 0 ? (
            <p className="text-sm text-slateblue">No submissions yet.</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(data.by_type).map(([type, count]) => (
                <div key={type}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-semibold">{type}</span>
                    <span className="text-slateblue">{count}</span>
                  </div>
                  <div className="h-2 bg-offwhite-dark">
                    <div className="h-full bg-orange transition-[width] duration-500" style={{ width: `${(count / maxType) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-sandline p-6" data-testid="by-status-card">
          <h2 className="font-heading font-bold text-lg tracking-tight mb-6">Pipeline Status</h2>
          <div className="space-y-4">
            {statuses.map((s) => (
              <div key={s} className="flex items-center gap-3">
                <span className={`w-3 h-3 ${statusColors[s]}`} />
                <span className="text-sm font-semibold capitalize flex-1">{s}</span>
                <span className="font-heading font-bold">{data.by_status[s] || 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
